import { getSupabaseClient } from "../supabase/index.js";
import { defaultTenantReferenceLists } from "./tenant-reference-lists.js";
import { normalizeReferenceListEntriesArray } from "./reference-list-entries.js";

function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {{ id?: string, label?: string }} row
 */
function entryAuditLabel(row) {
  return asStr(row?.label) || asStr(row?.id);
}

/**
 * @param {unknown} previousRows
 * @param {unknown} nextRows
 * @returns {Array<{
 *   action: string,
 *   entryId: string,
 *   entryLabel?: string,
 *   oldValue?: string,
 *   newValue?: string,
 * }>}
 */
export function diffReferenceListEntryChanges(previousRows, nextRows) {
  const prev = normalizeReferenceListEntriesArray(previousRows) || [];
  const next = normalizeReferenceListEntriesArray(nextRows) || [];
  const prevById = new Map(prev.map((row) => [row.id, row]));
  const nextById = new Map(next.map((row) => [row.id, row]));
  /** @type {ReturnType<typeof diffReferenceListEntryChanges>} */
  const changes = [];

  for (const [id, row] of nextById) {
    if (!prevById.has(id)) {
      changes.push({
        action: "entry_added",
        entryId: id,
        entryLabel: entryAuditLabel(row),
        newValue: entryAuditLabel(row),
      });
    }
  }

  for (const [id, row] of prevById) {
    if (!nextById.has(id)) {
      changes.push({
        action: "entry_removed",
        entryId: id,
        entryLabel: entryAuditLabel(row),
        oldValue: entryAuditLabel(row),
      });
    }
  }

  for (const [id, nextRow] of nextById) {
    const prevRow = prevById.get(id);
    if (!prevRow) continue;

    const wasEnabled = prevRow.enabled !== false;
    const isEnabled = nextRow.enabled !== false;
    if (wasEnabled !== isEnabled) {
      changes.push({
        action: isEnabled ? "entry_enabled" : "entry_disabled",
        entryId: id,
        entryLabel: entryAuditLabel(nextRow),
      });
    }

    const prevLabel = asStr(prevRow.label);
    const nextLabel = asStr(nextRow.label);
    if (prevLabel !== nextLabel) {
      changes.push({
        action: "entry_label_changed",
        entryId: id,
        entryLabel: entryAuditLabel(nextRow),
        oldValue: prevLabel || "—",
        newValue: nextLabel || "—",
      });
    }

    if (Boolean(prevRow.isDefault) !== Boolean(nextRow.isDefault) && nextRow.isDefault) {
      changes.push({
        action: "entry_default_changed",
        entryId: id,
        entryLabel: entryAuditLabel(nextRow),
        newValue: entryAuditLabel(nextRow),
      });
    }

    const prevSort = Number(prevRow.sort) || 0;
    const nextSort = Number(nextRow.sort) || 0;
    if (prevSort !== nextSort) {
      changes.push({
        action: "entry_reordered",
        entryId: id,
        entryLabel: entryAuditLabel(nextRow),
        oldValue: String(prevSort),
        newValue: String(nextSort),
      });
    }
  }

  return changes;
}

/**
 * @param {string} tenantId
 * @param {import('./tenant-reference-lists.js').TenantReferenceLists} previousLists
 * @param {import('./tenant-reference-lists.js').TenantReferenceLists} nextLists
 */
export function buildReferenceListAuditRows(tenantId, previousLists, nextLists) {
  const tid = asStr(tenantId);
  if (!tid) return [];

  const prev = previousLists && typeof previousLists === "object" ? previousLists : defaultTenantReferenceLists();
  const next = nextLists && typeof nextLists === "object" ? nextLists : defaultTenantReferenceLists();
  const keys = Object.keys(defaultTenantReferenceLists());
  /** @type {Record<string, unknown>[]} */
  const rows = [];

  for (const listKey of keys) {
    const changes = diffReferenceListEntryChanges(prev[listKey], next[listKey]);
    for (const change of changes) {
      rows.push({
        tenant_id: tid,
        list_key: listKey,
        entry_id: change.entryId,
        action: change.action,
        entry_label: change.entryLabel || null,
        old_value: change.oldValue || null,
        new_value: change.newValue || null,
      });
    }
  }

  return rows;
}

/**
 * @param {unknown} raw
 */
export function normalizeReferenceListAuditEntry(raw) {
  const row = raw && typeof raw === "object" ? raw : {};
  return {
    id: row.id,
    tenantId: asStr(row.tenantId ?? row.tenant_id),
    listKey: asStr(row.listKey ?? row.list_key),
    entryId: asStr(row.entryId ?? row.entry_id),
    action: asStr(row.action),
    entryLabel: asStr(row.entryLabel ?? row.entry_label),
    oldValue: asStr(row.oldValue ?? row.old_value),
    newValue: asStr(row.newValue ?? row.new_value),
    changedAt: row.changedAt ?? row.changed_at ?? null,
    changedByLabel: asStr(row.changedByLabel ?? row.changed_by_label) || "—",
  };
}

/**
 * @param {string} tenantId
 * @param {string} listKey
 */
export async function loadReferenceListAuditRemote(tenantId, listKey) {
  const tid = asStr(tenantId);
  const key = asStr(listKey);
  if (!tid) return { ok: false, reason: "missing-tenant" };
  if (!key) return { ok: false, reason: "missing-list-key" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc("trainerstunden_list_reference_list_audit", {
      p_tenant_id: tid,
      p_list_key: key,
    });

    if (error) {
      const code = asStr(error?.code);
      if (code === "PGRST202" || code === "42883") return { ok: false, reason: "rpc-missing" };
      return { ok: false, reason: "rpc-error" };
    }

    const payload = data && typeof data === "object" ? data : {};
    if (!payload.ok) return { ok: false, reason: asStr(payload.reason) || "rpc-failed" };

    const entries = Array.isArray(payload.entries) ? payload.entries : [];
    return { ok: true, entries: entries.map(normalizeReferenceListAuditEntry) };
  } catch {
    return { ok: false, reason: "exception" };
  }
}
